import {
  MdOpenInNew,
  MdOutlineFlag,
  MdOutlineLightbulb,
  MdOutlineSchedule,
} from "react-icons/md";
import { Badge, Card } from "../Primitives";
import { toneOf } from "../crmHelpers";

/*
  O que fazer com esta conta agora.

  O motivo e a ação sugerida existiam só para quem chegava pela fila. Quem abria a mesma
  conta pela tabela ou por um link via uma ficha sem nenhuma orientação. Aqui as mesmas
  regras do servidor respondem para a conta em qualquer caminho.
*/

const SNOOZE_TEXT = {
  TASK: "Já há tarefa aberta para esta conta.",
  CONTACT: "Contato recente: dá tempo de a conta responder antes de insistir.",
};

export const AlertList = ({ alerts = [] }) =>
  alerts.length ? (
    <ul className="space-y-1">
      {alerts.map((alert) => (
        <li
          key={alert.id}
          className={`flex flex-wrap items-center gap-x-2 gap-y-0.5 rounded-lg px-2.5 py-1.5 text-xs font-medium ring-1 ring-inset ${
            toneOf(alert.tone).badge
          }`}
        >
          <span>{alert.text}</span>
          {alert.paymentUrl ? (
            <a
              href={alert.paymentUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-0.5 font-semibold underline"
            >
              abrir fatura <MdOpenInNew size={12} />
            </a>
          ) : null}
        </li>
      ))}
    </ul>
  ) : null;

const NextActionCard = ({ guidance, isLoading, compact = false }) => {
  if (isLoading) {
    return (
      <Card className="p-4">
        <div className="h-4 w-40 animate-pulse rounded-full bg-slate-100" />
        <div className="mt-2 h-3 w-64 max-w-full animate-pulse rounded-full bg-slate-100" />
      </Card>
    );
  }

  const primary = guidance?.primary;
  const others = (guidance?.queues || []).filter(
    (queue) => queue.id !== primary?.id
  );
  const alerts = guidance?.alerts || [];

  if (!primary && !alerts.length) {
    // No painel lateral o espaço é curto: sem pendência, nada a dizer.
    if (compact) return null;

    return (
      <Card className="flex items-center gap-2.5 p-4">
        <span className="rounded-xl bg-emerald-50 p-2 text-emerald-600">
          <MdOutlineLightbulb size={18} />
        </span>
        <p className="text-sm text-slate-600">
          Nenhuma fila pede ação nesta conta agora.
        </p>
      </Card>
    );
  }

  return (
    <Card className={`space-y-2.5 ${compact ? "p-3" : "p-4"}`}>
      {primary ? (
        <div>
          <p className="flex flex-wrap items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
            <MdOutlineFlag size={13} /> Próxima ação
            <Badge tone={primary.tone}>{primary.label}</Badge>
          </p>
          <p className="mt-1 text-sm font-semibold text-slate-900">
            {primary.action}
          </p>
          <p className="mt-0.5 text-xs text-slate-500">{primary.why}</p>
          {primary.snoozedBy ? (
            <p className="mt-1 flex items-center gap-1 text-xs font-medium text-sky-700">
              <MdOutlineSchedule size={13} />
              {SNOOZE_TEXT[primary.snoozedBy]}
            </p>
          ) : null}
        </div>
      ) : null}

      <AlertList alerts={alerts} />

      {others.length ? (
        <p className="flex flex-wrap items-center gap-1 text-[11px] text-slate-500">
          Também em:
          {others.map((queue) => (
            <Badge key={queue.id} tone={queue.tone} className="!px-2 !py-0 !text-[10px]">
              {queue.label}
            </Badge>
          ))}
        </p>
      ) : null}
    </Card>
  );
};

export default NextActionCard;
